"use client";

import {useState} from "react";
import {Input} from "@/components/ui/input";
import {Button} from "@/components/ui/button";
import {useRouter} from "next/navigation";
import {Loader2, XIcon, Search, CircleAlert} from "lucide-react";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import useAnalytics from "@/hooks/useAnalytics";

const enum AnalyzerStatus {
  INITIAL = 'initial',
  SUBMITTING = 'submitting',
  ERROR = 'error',
}

class AnalyzerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalyzerError";
  }
}

let timeoutId: NodeJS.Timeout

const UrlAnalyzer = () => {
  const [url, setUrl] = useState("")
  const [error, setError] = useState('')
  const [status, setStatus] = useState(AnalyzerStatus.INITIAL)
  const router = useRouter()
  const { track } = useAnalytics();

  const handleAnalyzeClick = async (e: any) => {
    e?.preventDefault();
    try {
      track("analyze_url", { url });
      setError('');
      clearTimeout(timeoutId);
      setStatus(AnalyzerStatus.SUBMITTING);
      const urlRegex = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/;
      if (!urlRegex.test(url)) {
        throw new AnalyzerError("Please enter a valid URL");
      }
      const response = await fetch("/api/analyze", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url }), // Replace with actual URL
      });
      const { data, error } = await response.json();

      if (!response.ok) {
        if (response.status === 422 && error) {
          throw new AnalyzerError(error);
        }
        throw new AnalyzerError("Failed to analyze URL");
      }
      router.push(`/reports/${data.url_hash}`);
    } catch (error) {
      if (error instanceof AnalyzerError) {
        setError(error.message);
      } else {
        setError('Could not analyze the URL. Please try again.');
      }
      setStatus(AnalyzerStatus.ERROR);
      timeoutId = setTimeout(() => {
        setStatus(AnalyzerStatus.INITIAL);
      }, 3000);
    }
  }

  return (
    <form className="flex flex-col max-w-xl w-full gap-2 my-5 p-4 backdrop-blur-sm rounded-lg">
      <div className="flex items-center flex-col lg:flex-row gap-2">
        <Input
          className="py-4 px-3 h-10 bg-white text-primary"
          placeholder="Paste a news article URL here…"
          onChange={e => {
            setError('');
            setUrl(e.target.value);
          }}
        />
        <Button className="w-full lg:w-auto" size="lg" type="submit" variant="success" onClick={handleAnalyzeClick} disabled={status === AnalyzerStatus.SUBMITTING}>
          {status === AnalyzerStatus.SUBMITTING && <Loader2 className="animate-spin" />}
          {status === AnalyzerStatus.ERROR && <XIcon className="text-destructive" />}
          {status === AnalyzerStatus.INITIAL && <Search />}
          {status === AnalyzerStatus.SUBMITTING ? 'Fetching...' : 'Analyze'}
        </Button>
      </div>
      {error && (
        <Alert className="mb-4 text-left" variant="destructive">
          <AlertTitle className="flex gap-2 items-center font-semibold"><CircleAlert className="size-4" /><span>Error</span></AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </form>
  )
}

export default UrlAnalyzer;
